import React, { ElementType } from 'react';
import { Checkbox, CheckboxProps, FormControlLabel, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

type SaveCardConsentProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  // The merchant's consent wording in the customer's language, from save_card_config.label.
  label: string;
  // A recurring membership keeps the card to bill it every cycle, not to reuse it on a later purchase,
  // so it needs its own wording when the page carries no label of its own.
  isRecurring?: boolean;
  showError?: boolean;
  // Each surface brings its own theming: the widget passes its themed checkbox and typography,
  // the passes portal and the requested-order page use the MUI defaults.
  CheckboxComponent?: ElementType<CheckboxProps>;
  renderLabel?: (text: string) => React.ReactNode;
  errorColor?: string;
};

export const SaveCardConsent = ({
  checked,
  onChange,
  label,
  isRecurring,
  showError,
  CheckboxComponent = Checkbox,
  renderLabel = (text: string) => <Typography variant="body1">{text}</Typography>,
  errorColor,
}: SaveCardConsentProps) => {
  const { t } = useTranslation();

  return (
    <Grid container flexDirection="column">
      <FormControlLabel
        control={
          <CheckboxComponent checked={checked} onChange={(event) => onChange(event.target.checked)} size="medium" />
        }
        // A page can be configured with no wording (older config, or a language added after the fact).
        // Falling back keeps the promise visible instead of showing a bare checkbox. The recurring
        // wording mirrors DEFAULT_RECURRING_SAVE_CARD_CONSENT, which the backend stamps onto these
        // pages -- so a page the backfill has not reached yet still reads the same as one it has.
        label={renderLabel(
          label || t(isRecurring ? 'order_page.save_card_recurring_consent' : 'order_page.save_card_default_consent'),
        )}
      />
      {showError && !checked && (
        <Grid item color={errorColor || 'error.main'} mt={-1}>
          <Typography fontSize="12px">{t('order_page.save_card_required')}</Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default SaveCardConsent;
